import withStore, { TLoadingAwarePresenter, TPresentable } from "../withStore";
import Application from "../../Application";
import { IDynamicQuery } from "../../model/dynamicQuery/DynamicQuery";
import ShowErrorMessageInteractor from "../../interactor/notifications/ShowErrorMessageInteractor";
import CreateDynamicQueryInteractor from "../../interactor/dynamicQuery/CreateDynamicQueryInteractor";
import GetDynamicQueriesInteractor from "../../interactor/dynamicQuery/GetDynamicQueriesInteractor";
import UpdateDynamicQueryInteractor from "../../interactor/dynamicQuery/UpdateDynamicQueryInteractor";
import ShowSuccessMessageInteractor from "../../interactor/notifications/ShowSuccessMessageInteractor";
import DeleteDynamicQueryInteractor from "../../interactor/dynamicQuery/DeleteDynamicQueryInteractor";

export interface TDynamicQueriesPresenter extends TLoadingAwarePresenter {
  dynamicQueries: IDynamicQuery[];
}

export interface IDynamicQueriesPresenter
  extends TDynamicQueriesPresenter,
    TPresentable {
  setDynamicQueries(dynamicQueries: IDynamicQuery[] | undefined): void;
  createDynamicQuery(dynamicQuery: IDynamicQuery): void;
  updateDynamicQuery(dynamicQuery: IDynamicQuery): void;
  deleteDynamicQuery(dynamicQuery: IDynamicQuery): void;
}

const defaultState: TDynamicQueriesPresenter = {
  dynamicQueries: [] as IDynamicQuery[]
};

const DynamicQueriesPresenter = withStore<
  IDynamicQueriesPresenter,
  TDynamicQueriesPresenter
>(({ application, store, loader, translate }): IDynamicQueriesPresenter => {
  const _store = store;
  const _application: Application = application;
  const state = _store.getState<TDynamicQueriesPresenter>();

  loader.start("dynamicQueriesLoader");

  const setDynamicQueries = (dynamicQueries: IDynamicQuery[] | undefined) => {
    _store.update({
      dynamicQueries: dynamicQueries || ([] as IDynamicQuery[])
    });
    loader.stop("dynamicQueriesLoader");
  };

  const getDynamicQueries = async () => {
    return _application.container
      .resolve<GetDynamicQueriesInteractor>("getDynamicQueries")
      .execute();
  };

  const createDynamicQuery = async (dynamicQuery: IDynamicQuery) => {
    try {
      loader.start("dynamicQueriesLoader");

      await _application.container
        .resolve<CreateDynamicQueryInteractor>("createDynamicQuery")
        .execute(dynamicQuery);

      const dynamicQueries = await getDynamicQueries();

      _store.update({ dynamicQueries });

      loader.stop("dynamicQueriesLoader");

      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("dynamic_queries.create_success_message"));
    } catch (error) {
      loader.stop("dynamicQueriesLoader");
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };

  const updateDynamicQuery = async (dynamicQuery: IDynamicQuery) => {
    try {
      loader.start("dynamicQueriesLoader");

      await _application.container
        .resolve<UpdateDynamicQueryInteractor>("updateDynamicQuery")
        .execute(dynamicQuery.code, dynamicQuery);

      const dynamicQueries = await getDynamicQueries();

      _store.update({ dynamicQueries });

      loader.stop("dynamicQueriesLoader");

      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("dynamic_queries.update_success_message"));
    } catch (error) {
      loader.stop("dynamicQueriesLoader");
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };

  const deleteDynamicQuery = async (dynamicQuery: IDynamicQuery) => {
    try {
      loader.start("dynamicQueriesLoader");

      await _application.container
        .resolve<DeleteDynamicQueryInteractor>("deleteDynamicQuery")
        .execute(dynamicQuery.code);

      const dynamicQueries = await getDynamicQueries();

      _store.update({ dynamicQueries });

      loader.stop("dynamicQueriesLoader");

      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("dynamic_queries.update_success_message"));
    } catch (error) {
      loader.stop("dynamicQueriesLoader");
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };

  return {
    ...state,
    store: _store,
    loader,
    application: _application,
    translate,
    setDynamicQueries,
    createDynamicQuery,
    updateDynamicQuery,
    deleteDynamicQuery
  };
}, defaultState);

export default DynamicQueriesPresenter;
